'use client';

import React, { useState, useEffect } from 'react';
import Breadcrumb from '../../components/Breadcrumb';
import RecordAgentPanel from '../../components/crm/RecordAgentPanel';
import Tabs from '../../components/ui/Tabs';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Users, Mail, Phone, Edit2, Save, X, ArrowLeft, Star,
  Briefcase, Calendar, Activity, TrendingUp, Plus, CheckCircle,
  Clock, FileText, PhoneCall, Zap, AlertCircle,
  MapPin, Globe, LinkedinIcon, ExternalLink,
  MessageSquare, Shield, Crosshair, Award, Hash, Building2,
  ChevronRight, ArrowUpRight, ShieldCheck, Info, MoreVertical, ListChecks, Share2, UserCog, History, Instagram, Facebook
} from 'lucide-react';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import LeadWhatsAppPanel from '../../components/crm/LeadWhatsAppPanel';
import LeadSocialChatPanel from '../../components/crm/LeadSocialChatPanel';
import ConvertToDealModal from '../../components/crm/ConvertToDealModal';
import ApplyTemplateModal from '../../components/crm/ApplyTemplateModal';
import ShareModal from '../../components/crm/ShareModal';
import TransferOwnershipModal from '../../components/crm/TransferOwnershipModal';
import PathBar from '../../components/crm/PathBar';
import RelatedFiles from '../../components/crm/RelatedFiles';
import UnifiedTimeline from '../../components/crm/UnifiedTimeline';
import FormActivityPanel from '../../components/crm/FormActivityPanel';
import { formatDate, formatDateTime } from '../../utils/formatDate';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import { PageLoader } from '../../components/crm/ui';

const TABS = [
  { id: 'overview', label: 'Details', icon: Info },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { id: 'instagram', label: 'Instagram', icon: Instagram },
  { id: 'facebook', label: 'Facebook', icon: Facebook },
  { id: 'activities', label: 'Activity Feed', icon: Activity },
  { id: 'tasks', label: 'Pending Tasks', icon: Clock },
  { id: 'files', label: 'Files', icon: FileText },
  { id: 'formActivity', label: 'Form Activity', icon: FileText },
  { id: 'history', label: 'Timeline', icon: History },
];

// Fallback stage set for leads with no pipeline assigned yet — mirrors the
// default pipeline LeadService/DealService create on first use elsewhere.
const DEFAULT_STAGES = [
  { stageId: 'new', name: 'New', probability: 10 },
  { stageId: 'contacted', name: 'Contacted', probability: 25 },
  { stageId: 'qualified', name: 'Qualified', probability: 50 },
  { stageId: 'proposal', name: 'Proposal Sent', probability: 70 },
  { stageId: 'negotiation', name: 'Negotiation', probability: 85 },
];

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const companyCurrency = useCompanyCurrency();

  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [callingAgent, setCallingAgent] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  useEffect(() => { fetchLeadData(); }, [leadId]);

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/crm/leads/${leadId}`);
      if (response.data.success) {
        setLead(response.data.lead);
        setActivities(response.data.activities || []);
        setTasks(response.data.tasks || []);
        setFormData(response.data.lead);
      } else setError('Failed to load record.');
    } catch (err) {
      setError(err.response?.data?.message || 'Access denied.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaveLoading(true);
      await api.put(`/crm/leads/${leadId}`, formData);
      setLead(formData);
      setIsEditing(false);
      alertService.success('Success', 'Lead saved');
    } catch (err) {
      alertService.error('Error', 'Failed to update record');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleStageClick = async (stageId) => {
    if (!lead || stageId === lead.stage || saveLoading) return;
    try {
      setSaveLoading(true);
      const res = await api.patch(`/crm/leads/${leadId}/stage`, { stage: stageId });
      if (res.data.success) {
        setLead(res.data.lead);
        setFormData(res.data.lead);
        alertService.success('Stage Updated', 'Lead confidence recalculated for the new stage');
      }
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to move lead stage');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleConvertLead = () => setShowConvertModal(true);

  const handleConfirmConvert = async (pipelineId, accountName) => {
    try {
      const res = await api.post(`/crm/leads/${leadId}/convert`, {
        title: `Deal: ${lead.name}`,
        value: lead.expectedValue || 0,
        currency: lead.currency || companyCurrency,
        pipelineId,
        accountName: accountName || undefined
      });
      if (res.data.success) {
        setShowConvertModal(false);
        alertService.success('Converted', 'Lead successfully transitioned to Revenue Pipeline.');
        navigate('/dashboard/crm/deals');
      }
    } catch (err) {
      alertService.error('Conversion Failed', err.response?.data?.message || 'Action aborted');
    }
  };

  if (loading) {
    return (
      <PageLoader />
    );
  }

  if (!lead) {
    return (
      <div className="p-12 text-center space-y-4">
        <div className="w-16 h-16 bg-[#F4F6F9] rounded-full flex items-center justify-center mx-auto text-[#706E6B]">
          <AlertCircle size={32} />
        </div>
        <h2 className="text-xl font-bold text-[#080707]">Record Not Found</h2>
        <button onClick={() => navigate('/dashboard/crm/leads')} className="text-[#0176D3] font-bold text-sm hover:underline flex items-center justify-center gap-2 mx-auto">
          <ArrowLeft size={16} /> Return to Registry
        </button>
      </div>
    );
  }

  return (
    <div className="py-4 sm:py-6 px-0 sm:px-4 space-y-6 max-w-[100rem] mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500">
      
      {/* Dynamic Path Navigation */}
      <Breadcrumb className="mb-2" items={[{ label: 'Leads', to: '/dashboard/crm/leads' }, { label: lead.name }]} />

      <RecordAgentPanel entityType="lead" entityId={leadId} />

      {/* Profile Header */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 shrink-0 rounded-lg bg-[#0176D3] flex items-center justify-center text-white font-bold ">
              {lead.name?.slice(0, 2).toUpperCase()}
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <h1 className="text-lg sm:text-xl font-bold text-[#2E2E2E]">{isEditing ? 'Editing Profile' : lead.name}</h1>
                <span className="sf-badge sf-badge-primary">LEAD</span>
              </div>
              <div className="flex items-center gap-4 text-[12px] text-[#706E6B] font-medium">
                <span className="flex items-center gap-1.5"><Building2 size={12} /> {lead.company || 'No company'}</span>
                <span className="flex items-center gap-1.5"><MapPin size={12} /> {lead.city || 'Location Unknown'}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button onClick={() => setIsEditing(false)} className="h-9 px-4 bg-white border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F3F3] whitespace-nowrap shrink-0">Cancel</button>
                <button onClick={handleSave} disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] whitespace-nowrap shrink-0">Save</button>
              </>
            ) : (
              <>
                <button onClick={() => navigate(`/dashboard/linkedin/ai?skill=linkedin.dm.draft&leadId=${lead._id}`)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <LinkedinIcon size={14} /> Draft LinkedIn outreach
                </button>
                <button onClick={() => setIsEditing(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <Edit2 size={14} /> Edit
                </button>
                <button onClick={handleConvertLead} disabled={saveLoading} className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <Zap size={14} /> Convert to Deal
                </button>
                <button onClick={() => setShowApplyTemplate(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <ListChecks size={14} /> Apply Template
                </button>
                <button onClick={() => setShowShareModal(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <Share2 size={14} /> Share
                </button>
                <button onClick={() => setShowTransferModal(true)} className="h-9 px-4 bg-white text-[#0176D3] border border-[#C9C9C9] rounded text-[13px] font-semibold hover:bg-[#F3F2F2] flex items-center gap-2 whitespace-nowrap shrink-0">
                  <UserCog size={14} /> Transfer
                </button>
                <button className="h-9 w-9 flex items-center justify-center bg-white border border-[#D8DDE6] rounded text-[#706E6B] hover:bg-[#F3F2F2]">
                  <MoreVertical size={16} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Pipeline Progression — clicking a stage moves the lead there and
            recalculates its confidence % (probability) from the matching
            pipeline stage, same pattern as the Deal detail page. */}
        {lead.status !== 'converted' && lead.status !== 'lost' && (
          <div className="mt-8 pt-6 border-t border-[#D8DDE6] space-y-3">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest">Pipeline Progression</p>
            {(() => {
              const rawStages = lead.pipelineId?.stages?.length ? lead.pipelineId.stages : DEFAULT_STAGES;
              const idOf = (x) => (typeof x === 'string' ? x : (x.stageId || x.name));
              const stages = rawStages.map(s => ({ id: idOf(s), label: typeof s === 'string' ? s : s.name }));
              const currentIdx = stages.findIndex(s => s.id === lead.stage);
              return (
                <PathBar
                  stages={stages}
                  currentIndex={currentIdx}
                  onStageClick={handleStageClick}
                  disabled={saveLoading}
                />
              );
            })()}
          </div>
        )}

        {/* Highlight Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-[#D8DDE6]">
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Status</p>
            <p className="text-[13px] font-bold text-[#0176D3]">{lead.status?.toUpperCase() || 'NEW'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Expected Value</p>
            <p className="text-[13px] font-bold text-[#080707]">{formatCurrency(lead.expectedValue || 0, lead.currency || companyCurrency)}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Lead Source</p>
            <p className="text-[13px] font-bold text-[#080707]">{lead.source?.toUpperCase() || 'MANUAL'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Owner</p>
            <p className="text-[13px] font-bold text-[#080707]">{lead.assignedTo || 'Unassigned'}</p>
          </div>
        </div>

        {/* Ownership & Audit */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-[#D8DDE6]">
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Record Owner</p>
            <p className="text-[13px] font-medium text-[#080707]">{lead.ownerName || 'Unassigned'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Created By</p>
            <p className="text-[13px] font-medium text-[#080707]">{lead.createdByName || '—'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Last Modified By</p>
            <p className="text-[13px] font-medium text-[#080707]">{lead.updatedByName || '—'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wider">Modified On</p>
            <p className="text-[13px] font-medium text-[#706E6B]">{lead.updatedAt ? formatDate(lead.updatedAt) : '—'}</p>
          </div>
        </div>
      </div>

      {/* Tabs Control */}
      <Tabs tabs={TABS.map((t) => ({ ...t, key: t.id }))} value={activeTab} onChange={setActiveTab} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Main Content Area */}
        <div className="lg:col-span-8 space-y-6">
          {activeTab === 'overview' && (
            <div className="sf-card p-8 bg-white space-y-8 animate-in fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Full Legal Name</label>
                  {isEditing ? (
                    <input className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px]" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">{lead.name}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Mobile Number</label>
                  {isEditing ? (
                    <input className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px]" value={formData.phoneNumber} onChange={e => setFormData({...formData, phoneNumber: e.target.value})} />
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">{lead.phoneNumber || '—'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Primary Email</label>
                  {isEditing ? (
                    <input className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px]" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">{lead.email || '—'}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Industry Sector</label>
                  <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1 uppercase">{lead.source}</p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-[#706E6B] uppercase">Visibility</label>
                  {isEditing ? (
                    <select className="w-full h-9 px-3 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]" value={formData.visibility || 'team'} onChange={e => setFormData({...formData, visibility: e.target.value})}>
                      <option value="team">Team — everyone can see this</option>
                      <option value="private">Private — only owner and shared users</option>
                    </select>
                  ) : (
                    <p className="text-[14px] font-medium text-[#080707] border-b border-dashed border-[#D8DDE6] pb-1">{lead.visibility === 'private' ? 'Private' : 'Team'}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-semibold text-[#706E6B]">Internal Narrative (Notes)</label>
                <div className="p-4 bg-[#F4F6F9] rounded border border-[#D8DDE6] text-[13px] text-[#080707] min-h-[120px] shadow-inner font-medium space-y-3">
                  {Array.isArray(lead.notes) && lead.notes.length > 0 ? (
                    lead.notes.map((n, i) => (
                      <div key={n._id || i} className="pb-2 border-b border-dashed border-[#D8DDE6] last:border-b-0 last:pb-0">
                        <p className="whitespace-pre-wrap">{n.text}</p>
                        {n.createdAt && (
                          <p className="text-[10px] text-[#706E6B] font-bold uppercase tracking-wider mt-1">
                            {formatDateTime(n.createdAt)}
                          </p>
                        )}
                      </div>
                    ))
                  ) : typeof lead.notes === 'string' && lead.notes ? (
                    <p className="whitespace-pre-wrap">{lead.notes}</p>
                  ) : (
                    'No historical narrative recorded for this entity node.'
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'whatsapp' && (
            <div className="animate-in fade-in">
              <LeadWhatsAppPanel leadId={leadId} phoneNumber={lead.phoneNumber} />
            </div>
          )}

          {activeTab === 'instagram' && (
            <div className="animate-in fade-in">
              <LeadSocialChatPanel leadId={leadId} channel="instagram" />
            </div>
          )}

          {activeTab === 'facebook' && (
            <div className="animate-in fade-in">
              <LeadSocialChatPanel leadId={leadId} channel="messenger" />
            </div>
          )}

          {activeTab === 'activities' && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4">
              {activities.length > 0 ? activities.map(a => (
                <div key={a._id} className="sf-card p-4 flex gap-4 hover:border-[#0176D3] transition-all">
                  <div className="w-10 h-10 rounded border border-[#D8DDE6] bg-[#F4F6F9] flex items-center justify-center shrink-0">
                    <Activity size={18} className="text-[#0176D3]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-[14px] font-bold text-[#080707] translate-tight">{a.title}</h4>
                      <span className="text-[11px] font-bold text-[#706E6B]">{formatDate(a.createdAt)}</span>
                    </div>
                    <p className="text-[12px] text-[#706E6B] line-clamp-2">{a.description}</p>
                  </div>
                </div>
              )) : (
                <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded h-full bg-white opacity-40">
                  <Activity size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">Feed Secure / No activity</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'tasks' && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4">
              {tasks.length > 0 ? tasks.map(t => (
                <div
                  key={t._id}
                  onClick={() => navigate(`/dashboard/crm/tasks/${t._id}`)}
                  className="sf-card p-4 flex items-center justify-between hover:border-[#0176D3] transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full border-2 border-[#D8DDE6] flex items-center justify-center text-[#706E6B]">
                      <CheckCircle size={18} />
                    </div>
                    <div>
                      <h4 className="text-[14px] font-bold text-[#080707] hover:text-[#0176D3]">{t.title}</h4>
                      <p className="text-[11px] text-[#BA0517] font-bold uppercase tracking-widest">Due: {formatDate(t.dueDate)}</p>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="py-24 text-center border-2 border-dashed border-[#D8DDE6] rounded h-full bg-white opacity-40">
                  <Clock size={48} className="mx-auto mb-4 opacity-10" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">No open tasks</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'files' && (
            <div className="sf-card p-4 animate-in slide-in-from-bottom-4">
              <RelatedFiles module="leads" moduleId={leadId} recordName={lead?.name} />
            </div>
          )}

          {activeTab === 'formActivity' && (
            <div className="sf-card p-4 animate-in slide-in-from-bottom-4">
              <FormActivityPanel recordType="lead" recordId={leadId} />
            </div>
          )}

          {activeTab === 'history' && (
            <UnifiedTimeline activities={activities} tasks={tasks} />
          )}
        </div>

        {/* Sidebar Intelligence */}
        <div className="lg:col-span-4 space-y-6">
          <div className="sf-card p-6 bg-white space-y-6">
            <h3 className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest border-b border-[#D8DDE6] pb-3 mb-4">Notes</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Lead Score</span>
                <span className="sf-badge sf-badge-primary">{lead.score || 0} Points</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Confidence (Stage)</span>
                <span className="sf-badge sf-badge-success">{lead.probability ?? 0}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Campaign</span>
                <span className="text-[12px] font-bold text-[#080707]">{lead.utmCampaign || 'Organic Flow'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Assignment</span>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#0176D3] text-white flex items-center justify-center text-[8px] font-bold">U</div>
                  <span className="text-[12px] font-bold text-[#080707] underline underline-offset-4 decoration-[#0176D3]/30">{lead.assignedTo || 'WaapLeads Agent'}</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => setActiveTab('whatsapp')}
              className="w-full h-12 bg-[#0176D3] text-white rounded font-bold text-[13px] uppercase tracking-wider hover:bg-[#014486] transition-all shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex items-center justify-center gap-2 mt-6"
            >
              <MessageSquare size={18} /> Initiate WhatsApp
            </button>
          </div>

          <div className="sf-card p-6 bg-[#F4F6F9] border border-dashed border-[#D8DDE6] space-y-2">
             <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-widest flex items-center gap-2"><ShieldCheck size={14} className="text-[#2E844A]" /> Lifecycle Tracking</p>
             <p className="text-[12px] text-[#080707] font-medium leading-relaxed italic opacity-80">This entity was synchronized via the {lead.source} gateway and is currently being processed by the Revenue Command Hub.</p>
          </div>
        </div>
      </div>

      <ConvertToDealModal
        open={showConvertModal}
        lead={lead}
        onClose={() => setShowConvertModal(false)}
        onConfirm={handleConfirmConvert}
      />
      <ApplyTemplateModal
        isOpen={showApplyTemplate}
        onClose={() => setShowApplyTemplate(false)}
        leadId={lead._id}
      />
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        resourceType="lead"
        resourceId={lead._id}
        resourceLabel="Lead"
      />
      <TransferOwnershipModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        resourceType="lead"
        resourceId={lead._id}
        currentOwnerId={lead.ownerId}
        onTransferred={() => fetchLeadData()}
      />
    </div>
  );
}
